import { useState } from 'react';

import {
  captureExamEvent,
  EXAM_EVENTS,
  getSecondsSinceAnalysisReceived,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { EyesPosition } from 'src/models/Session.model';

import AnalyzingFooter from './AnalyzingFooter';
import GradableFooter from './GradableFooter';
import SavingFooter from './SavingFooter';
import ServerErrorFooter from './ServerErrorFooter';
import UngradableFooter from './UngradableFooter';

interface FooterControllerProps {
  isAnalyzing: boolean;
  hasServerError: boolean;
  gradableEyesPosition: EyesPosition | undefined;
  shouldShowDilationRecommendation: boolean;
  handleRetake: (position: EyesPosition) => void;
  handleCloseAnalyzingProgressModal: () => void;
  completeExam: () => Promise<boolean>;
  saveAndDilate: () => void;
  onRequestCancelExam: () => void;
}

export default function FooterController({
  isAnalyzing,
  hasServerError,
  gradableEyesPosition,
  shouldShowDilationRecommendation,
  handleRetake,
  handleCloseAnalyzingProgressModal,
  completeExam,
  saveAndDilate,
  onRequestCancelExam,
}: FooterControllerProps) {
  const { session } = useRemoteBoundStore();

  const [isCompletingExam, setIsCompletingExam] = useState(false);

  const handleCompleteExam = async () => {
    const secondsSinceAnalysisReceived = getSecondsSinceAnalysisReceived();
    const order_id = session.orderId;
    const exam_id = session.iid;
    setIsCompletingExam(true);
    const ok = await completeExam();
    setIsCompletingExam(false);
    if (!ok) return;
    captureExamEvent(EXAM_EVENTS.EXAM_COMPLETED, {
      order_id,
      exam_id,
      left_image_gradable: gradableEyesPosition === EyesPosition.LEFT,
      right_image_gradable: gradableEyesPosition === EyesPosition.RIGHT,
      overall_gradable: gradableEyesPosition === EyesPosition.BOTH,
      ...(secondsSinceAnalysisReceived !== undefined
        ? { since_analysis_received_seconds: secondsSinceAnalysisReceived }
        : {}),
    });
  };

  const handleSaveAndDilate = () => {
    const order_id = session.orderId;
    const exam_id = session.iid;
    saveAndDilate();
    captureExamEvent(EXAM_EVENTS.EXAM_SAVED_AND_DILATED, { order_id, exam_id });
  };

  if (isAnalyzing) {
    return <AnalyzingFooter />;
  }

  if (isCompletingExam) {
    return <SavingFooter />;
  }

  if (gradableEyesPosition === EyesPosition.BOTH) {
    return <GradableFooter onCompleteExam={handleCompleteExam} />;
  }

  if (hasServerError) {
    return <ServerErrorFooter onEndSession={onRequestCancelExam} />;
  }

  return (
    <UngradableFooter
      shouldShowDilationRecommendation={shouldShowDilationRecommendation}
      gradableEyesPosition={gradableEyesPosition}
      onEndSession={onRequestCancelExam}
      onCompleteExam={handleCompleteExam}
      onSaveAndDilate={handleSaveAndDilate}
      handleRetake={handleRetake}
      handleCloseAnalyzingProgressModal={handleCloseAnalyzingProgressModal}
    />
  );
}
